import { PresenceFilter, ReviewSearchParamsDto } from '@/contracts/marketplace/reviews';
import { BrandGroupEntity } from '@/entities/brand-group.entity';
import { ProductGroupEntity } from '@/entities/product-group.entity';
import { Marketplace } from '@/enums/marketplace';
import { useDebounce } from '@/hooks/use-debounce';
import type { RuntimeSupportedMarketplace } from '@/shared/marketplace';
import { formatProductGroupDropdownInfo } from '@/shared/format-product-dropdown-info';
import { areReviewFiltersEquivalent, normalizeReviewFilters } from '@/shared/review-filters-normalization';
import { SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';

type UseFeedbacksTableFiltersArgs = {
    defaultFilterValues: ReviewSearchParamsDto;
    onFiltersChange: (filters: SetStateAction<ReviewSearchParamsDto>) => void;
    products: ProductGroupEntity[];
    brandGroups: BrandGroupEntity[];
};

export const ratingFilterValues = ['1', '2', '3', '4', '5'] as const;
const ratingFilterValuesSet = new Set<string>(ratingFilterValues);
export const platformFilterValues = [Marketplace.Wb, Marketplace.Ozon] as const;
const platformFilterValuesSet = new Set<string>(platformFilterValues);
const isPlatformFilterValue = (value: string): value is RuntimeSupportedMarketplace =>
    platformFilterValuesSet.has(value);

const parseRatingFilter = (value?: string): string[] => {
    if (!value) return [];

    const parsedValues = value
        .split(',')
        .map((item) => item.trim())
        .filter((item) => ratingFilterValuesSet.has(item));

    const uniqueValues = Array.from(new Set(parsedValues));
    uniqueValues.sort((left, right) => Number(left) - Number(right));

    return uniqueValues;
};

const serializeRatingFilter = (values: string[]): string | undefined => {
    const validValues = values.filter((value) => ratingFilterValuesSet.has(value));
    const uniqueSortedValues = Array.from(new Set(validValues)).sort((left, right) => Number(left) - Number(right));

    return uniqueSortedValues.length ? uniqueSortedValues.join(',') : undefined;
};

const parsePlatformFilter = (value?: string): RuntimeSupportedMarketplace[] => {
    if (!value) return [];

    const parsedValues = value
        .split(',')
        .map((item) => item.trim())
        .filter(isPlatformFilterValue);

    const uniqueSortedValues = Array.from(new Set(parsedValues)).sort(
        (left, right) => platformFilterValues.indexOf(left) - platformFilterValues.indexOf(right),
    );

    return uniqueSortedValues;
};

const serializePlatformFilter = (values: string[]): string | undefined => {
    const validValues = values.filter(isPlatformFilterValue);
    const uniqueSortedValues = Array.from(new Set(validValues)).sort(
        (left, right) => platformFilterValues.indexOf(left) - platformFilterValues.indexOf(right),
    );

    return uniqueSortedValues.length ? uniqueSortedValues.join(',') : undefined;
};

export const useFeedbacksTableFilters = ({
    defaultFilterValues,
    onFiltersChange,
    products,
    brandGroups,
}: UseFeedbacksTableFiltersArgs) => {
    const canonicalFilters = useMemo(() => normalizeReviewFilters(defaultFilterValues), [defaultFilterValues]);

    const rating = canonicalFilters.rating;
    const convertedStatus = canonicalFilters.convertedStatus;
    const hasPhotos = canonicalFilters.hasPhotos;
    const hasAuthor = canonicalFilters.hasAuthor;
    const hasReviewText = canonicalFilters.hasReviewText;
    const hasAnswerText = canonicalFilters.hasAnswerText;
    const hasAiResponse = canonicalFilters.hasAiResponse;
    const productGroupId = canonicalFilters.productId;
    const brandGroupId = canonicalFilters.brandName;
    const createdDate = canonicalFilters.createdDate || '';
    const answerUpdatedDate = canonicalFilters.answerUpdatedDate || '';
    const platform = canonicalFilters.platform;

    const reviewTextValue = canonicalFilters.reviewText ?? '';
    const answerTextValue = canonicalFilters.answerText ?? '';

    const [reviewTextDraft, setReviewTextDraft] = useState<string>(reviewTextValue);
    const [answerTextDraft, setAnswerTextDraft] = useState<string>(answerTextValue);
    const [productSearch, setProductSearch] = useState<string | undefined>();
    const [brandSearch, setBrandSearch] = useState<string | undefined>();
    const isExternalReviewTextSyncRef = useRef(false);
    const isExternalAnswerTextSyncRef = useRef(false);

    const debouncedReviewTextDraft = useDebounce<string>(reviewTextDraft, 500);
    const debouncedAnswerTextDraft = useDebounce<string>(answerTextDraft, 500);

    useEffect(() => {
        isExternalReviewTextSyncRef.current = true;
        setReviewTextDraft(reviewTextValue);
    }, [reviewTextValue]);

    useEffect(() => {
        isExternalAnswerTextSyncRef.current = true;
        setAnswerTextDraft(answerTextValue);
    }, [answerTextValue]);

    const emitFiltersPatch = useCallback(
        (patch: Partial<ReviewSearchParamsDto>) => {
            onFiltersChange((currentFilters) => {
                const nextFilters = normalizeReviewFilters({
                    ...currentFilters,
                    ...patch,
                });

                if (areReviewFiltersEquivalent(currentFilters, nextFilters)) {
                    return currentFilters;
                }

                return nextFilters;
            });
        },
        [onFiltersChange],
    );

    useEffect(() => {
        if (isExternalReviewTextSyncRef.current) {
            if (debouncedReviewTextDraft === reviewTextValue) {
                isExternalReviewTextSyncRef.current = false;
            }
            return;
        }

        if (debouncedReviewTextDraft === reviewTextValue) {
            return;
        }

        emitFiltersPatch({
            reviewText: debouncedReviewTextDraft,
        });
    }, [debouncedReviewTextDraft, emitFiltersPatch, reviewTextValue]);

    useEffect(() => {
        if (isExternalAnswerTextSyncRef.current) {
            if (debouncedAnswerTextDraft === answerTextValue) {
                isExternalAnswerTextSyncRef.current = false;
            }
            return;
        }

        if (debouncedAnswerTextDraft === answerTextValue) {
            return;
        }

        emitFiltersPatch({
            answerText: debouncedAnswerTextDraft,
        });
    }, [answerTextValue, debouncedAnswerTextDraft, emitFiltersPatch]);

    const filteredProductGroups = useMemo(() => {
        if (!productSearch) return products;

        const normalizedSearch = productSearch.trim().toLowerCase();
        if (!normalizedSearch) return products;

        return products.filter(
            (product) =>
                product.name.toLowerCase().includes(normalizedSearch) ||
                product.brandName?.toLowerCase().includes(normalizedSearch) ||
                String(product.productCount).includes(normalizedSearch),
        );
    }, [products, productSearch]);

    const preparedProductGroups = useMemo(() => {
        return filteredProductGroups.map((product) => ({
            name: product.name,
            value: product.id,
            info: formatProductGroupDropdownInfo(product.productCount, product.brandName),
        }));
    }, [filteredProductGroups]);

    const preparedBrands = useMemo(() => {
        const uniqueBrands = [...brandGroups].sort((leftBrandGroup, rightBrandGroup) =>
            leftBrandGroup.name.localeCompare(rightBrandGroup.name, 'ru', { sensitivity: 'base', numeric: true }),
        );

        const normalizedSearch = brandSearch?.trim().toLowerCase();
        const visibleBrands = normalizedSearch
            ? uniqueBrands.filter((currentBrandGroup) =>
                  currentBrandGroup.name.toLowerCase().includes(normalizedSearch),
              )
            : uniqueBrands;

        return visibleBrands.map((currentBrandGroup) => ({
            name: currentBrandGroup.name,
            value: currentBrandGroup.id,
        }));
    }, [brandGroups, brandSearch]);

    const selectedRatings = useMemo(() => parseRatingFilter(rating), [rating]);
    const selectedPlatforms = useMemo(() => parsePlatformFilter(platform), [platform]);

    const onRatingFilterChange = (nextValues: string[]) => {
        emitFiltersPatch({ rating: serializeRatingFilter(nextValues) });
    };

    const onPlatformFilterChange = (nextValues: string[]) => {
        emitFiltersPatch({ platform: serializePlatformFilter(nextValues) });
    };

    const setRating = (value: string | undefined) => {
        emitFiltersPatch({ rating: value });
    };

    const setConvertedStatus = (value: string | undefined) => {
        emitFiltersPatch({ convertedStatus: value });
    };

    const setPlatform = (value: string | undefined) => {
        emitFiltersPatch({ platform: value });
    };

    const setHasPhotos = (value: PresenceFilter | undefined) => {
        emitFiltersPatch({ hasPhotos: value });
    };

    const setHasAuthor = (value: PresenceFilter | undefined) => {
        emitFiltersPatch({ hasAuthor: value });
    };

    const setHasReviewText = (value: PresenceFilter | undefined) => {
        emitFiltersPatch({ hasReviewText: value });
    };

    const setHasAnswerText = (value: PresenceFilter | undefined) => {
        emitFiltersPatch({ hasAnswerText: value });
    };

    const setHasAiResponse = (value: PresenceFilter | undefined) => {
        emitFiltersPatch({ hasAiResponse: value });
    };

    const setProductGroupId = (value: string | undefined) => {
        emitFiltersPatch({ productId: value });
    };

    const setBrandGroupId = (value: string | undefined) => {
        emitFiltersPatch({ brandName: value });
    };

    const setCreatedDate = (value: string) => {
        emitFiltersPatch({ createdDate: value });
    };

    const setAnswerUpdatedDate = (value: string) => {
        emitFiltersPatch({ answerUpdatedDate: value });
    };

    return {
        rating,
        convertedStatus,
        hasPhotos,
        hasAuthor,
        hasReviewText,
        hasAnswerText,
        hasAiResponse,
        productGroupId,
        reviewText: reviewTextDraft,
        answerText: answerTextDraft,
        brandGroupId,
        createdDate,
        answerUpdatedDate,
        platform,
        productSearch,
        brandSearch,
        preparedProductGroups,
        preparedBrands,
        selectedRatings,
        selectedPlatforms,
        setRating,
        setConvertedStatus,
        setPlatform,
        onPlatformFilterChange,
        setHasPhotos,
        setHasAuthor,
        setHasReviewText,
        setHasAnswerText,
        setHasAiResponse,
        setProductGroupId,
        setReviewText: setReviewTextDraft,
        setAnswerText: setAnswerTextDraft,
        setBrandGroupId,
        setCreatedDate,
        setAnswerUpdatedDate,
        setProductSearch,
        setBrandSearch,
        onRatingFilterChange,
    };
};
